import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ClipboardList, PlayCircle, CheckCircle2, AlertCircle } from 'lucide-react'
import { getStatusPillClass } from './RecoveryTrackingHeader'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import type { ViewConflictRow, ViewConflictSide } from '../hooks'
import { appLocale } from '@/lib/dateUtils'

function formatVaultDate(iso: string | null | undefined): string {
    if (!iso) return '—'
    try {
        const d = new Date(iso)
        return d.toLocaleDateString(appLocale(), { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch {
        return String(iso)
    }
}

function DoughnutChart({ percentage, size = 32 }: { percentage: number; size?: number }) {
    const r = (size - 4) / 2
    const circumference = 2 * Math.PI * r
    const pct = Math.min(100, Math.max(0, percentage))
    const strokeDashoffset = circumference * (1 - pct / 100)
    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size }} title={`${Math.round(pct)}%`}>
            <svg width={size} height={size} className="rotate-[-90deg]">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={3} className="text-muted-foreground/30" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="text-green-600 transition-all duration-300"
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-foreground">{Math.round(pct)}%</span>
        </div>
    )
}

function RunbookStatsBlock({ side, t }: { side: ViewConflictSide; t: (key: string) => string }) {
    const noTasks = side.totalTasks ?? 0
    if (noTasks === 0) {
        return <span className="text-xs text-muted-foreground">{t('recoverRequests.noRunbook')}</span>
    }
    const noStarted = side.startedTasks ?? 0
    const noCompleted = side.completedTasks ?? 0
    const noIssues = side.issueTasks ?? 0
    const pct = side.percentComplete ?? (noTasks > 0 ? (noCompleted / noTasks) * 100 : 0)
    return (
        <div className="flex items-center gap-2 flex-nowrap">
            <DoughnutChart percentage={pct} size={36} />
            <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-primary/10">
                    <ClipboardList className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-medium">{noTasks}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/10">
                    <PlayCircle className="h-3.5 w-3.5 text-amber-600" />
                    <span className="text-xs font-medium">{noStarted}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-green-500/10">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs font-medium">{noCompleted}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-red-500/10">
                    <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                    <span className="text-xs font-medium">{noIssues}</span>
                </div>
            </div>
        </div>
    )
}

/** Only treat as "has assignment status" when non-empty and not placeholder (null/none) */
function hasAssignmentStatus(s: string | null | undefined): boolean {
    if (s == null) return false
    const t = String(s).trim()
    return t !== '' && !/^(null|none)$/i.test(t)
}

/** Normalize for same-version comparison (both must be valid numbers and equal) */
function sameObjectVersion(manifestId: number | null | undefined, externalId: number | null | undefined): boolean {
    const m = manifestId != null ? Number(manifestId) : NaN
    const e = externalId != null ? Number(externalId) : NaN
    return !Number.isNaN(m) && !Number.isNaN(e) && m === e
}

/** Parse vault datetime to timestamp for comparison; null if missing/invalid */
function parseVaultTime(iso: string | null | undefined): number | null {
    if (iso == null || String(iso).trim() === '') return null
    try {
        const d = new Date(iso)
        return Number.isNaN(d.getTime()) ? null : d.getTime()
    } catch {
        return null
    }
}

export function ConflictResolutionModal(props: {
    open: boolean
    onOpenChange: (open: boolean) => void
    requestId: string
    /** Display label for the incoming request (e.g. "AD1 – 11 Mar 2026, 11:05") instead of UUID */
    incomingRequestLabel?: string
    /** Recovery set name for the "Recovery Set" column heading */
    recoverySetName?: string
    conflicts: ViewConflictRow[]
    onSubmit: (resolutions: Array<{ objectId: string; priorityRequestId: string }>) => void
    isSubmitting: boolean
}) {
    const { t } = useTranslation()
    const { open, onOpenChange, requestId, incomingRequestLabel, recoverySetName, conflicts, onSubmit, isSubmitting } = props
    const [selections, setSelections] = useState<Record<string, string>>({})

    useEffect(() => {
        if (open && conflicts.length > 0) {
            const initial: Record<string, string> = {}
            for (const c of conflicts) {
                const matching = sameObjectVersion(c.manifest.objectversionsId, c.external.objectversionsId)
                if (matching && c.manifestRequestId != null) {
                    initial[c.objectId] = c.manifestRequestId
                } else {
                    const extTime = parseVaultTime(c.external.vaultdatetime)
                    const manTime = parseVaultTime(c.manifest.vaultdatetime)
                    const manifestIsNewer = manTime != null && (extTime == null || manTime > extTime)
                    if (manifestIsNewer && c.manifestRequestId != null) {
                        initial[c.objectId] = c.manifestRequestId
                    } else {
                        initial[c.objectId] = c.externalRequestId
                    }
                }
            }
            setSelections(initial)
        }
    }, [open, conflicts])

    const allResolved =
        conflicts.length > 0 &&
        conflicts.every((c) => {
            const sel = selections[c.objectId]
            return sel != null && sel !== ''
        })

    const handleSubmit = () => {
        if (!allResolved) return
        const resolutions = conflicts.map((c) => ({
            objectId: c.objectId,
            priorityRequestId: selections[c.objectId]!,
        }))
        onSubmit(resolutions)
        onOpenChange(false)
    }

    const requestLabel = incomingRequestLabel ?? requestId

    const description = `Request "${requestLabel}" has objects that conflict with requests already in this set. Choose which version to use for each object.`

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl" aria-describedby="conflict-resolution-description">
                <DialogHeader>
                    <DialogTitle>{t('recoverRequests.conflictResolution.title', 'Resolve conflicts')}</DialogTitle>
                    <DialogDescription id="conflict-resolution-description">
                        {description}
                    </DialogDescription>
                </DialogHeader>
                <div className="py-2 overflow-x-auto">
                    <div className="rounded-md border overflow-hidden min-w-0">
                        <div className="grid grid-cols-[minmax(0,1fr)_minmax(180px,1fr)_minmax(220px,1fr)_minmax(220px,1fr)] gap-2 bg-muted/50 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            <div />
                            <div />
                            <div className="min-w-0">
                                <div className="text-foreground">{t('recoverRequests.conflictResolution.requestIdHeading', 'Request ID')}</div>
                                {requestLabel && <div className="font-normal normal-case truncate text-[11px] mt-0.5">{requestLabel}</div>}
                            </div>
                            <div className="min-w-0">
                                <div className="text-foreground">{t('recoverRequests.conflictResolution.recoverySetHeading', 'Recovery Set')}</div>
                                {recoverySetName && <div className="font-normal normal-case truncate text-[11px] mt-0.5">{recoverySetName}</div>}
                            </div>
                        </div>
                        {conflicts.map((c) => {
                                    const selected = selections[c.objectId] ?? c.externalRequestId
                                    const selectExisting = c.manifestRequestId != null && selected === c.manifestRequestId
                                    const selectNew = selected === c.externalRequestId
                                    const sameVersion = sameObjectVersion(c.manifest.objectversionsId, c.external.objectversionsId)
                                    const extTime = parseVaultTime(c.external.vaultdatetime)
                                    const manTime = parseVaultTime(c.manifest.vaultdatetime)
                                    const bothDatesValid = extTime != null && manTime != null
                                    const datesDiffer = bothDatesValid && extTime !== manTime
                                    const externalIsNewer = bothDatesValid && extTime > manTime
                                    const manifestIsNewer = bothDatesValid && manTime > extTime
                                    return (
                                        <div
                                            key={c.objectId}
                                            className="grid grid-cols-[minmax(0,1fr)_minmax(180px,1fr)_minmax(220px,1fr)_minmax(220px,1fr)] gap-2 items-stretch px-3 py-2 border-t border-border"
                                        >
                                            <div className="flex items-center gap-2 min-w-0 py-0.5">
                                                {c.iconFilename && (
                                                    <EntityIcon src={buildIconPath(c.iconFilename)} alt="" className="h-7 w-7 shrink-0" />
                                                )}
                                                <div className="flex flex-col min-w-0 justify-center">
                                                    <span className="text-sm font-medium text-foreground truncate">{c.objectId}</span>
                                                    {c.objectName != null && c.objectName !== '' && (
                                                        <span className="text-xs text-muted-foreground truncate">{c.objectName}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col min-w-0 py-0.5 gap-0.5">
                                                {sameVersion ? (
                                                    <>
                                                        <span className="text-xs text-muted-foreground">{t('recoverRequests.conflictResolution.sameVersion', 'Matching version')}</span>
                                                        <span className="text-xs break-words">
                                                            <span className="text-muted-foreground">{t('recoverRequests.conflictResolution.dateVaultedLabel', 'Date Vaulted:')}</span>{' '}
                                                            <span className="text-foreground">
                                                                {c.manifest.vaultdatetime != null && String(c.manifest.vaultdatetime).trim() !== ''
                                                                    ? formatVaultDate(c.manifest.vaultdatetime)
                                                                    : t('recoverRequests.conflictResolution.managementOnly', 'Management Only')}
                                                            </span>
                                                        </span>
                                                    </>
                                                ) : null}
                                            </div>
                                            {/* Request ID column */}
                                            <div className="flex min-w-0 rounded border border-border bg-muted/20 p-1.5 items-center justify-between gap-2">
                                                <div className="flex flex-col min-w-0 gap-0.5 flex-1">
                                                    {hasAssignmentStatus(c.external.assignmentStatus) && (
                                                        <span className={cn('text-xs font-medium rounded px-1.5 py-0.5 w-fit', getStatusPillClass(c.external.assignmentStatus!))}>{c.external.assignmentStatus}</span>
                                                    )}
                                                    {hasAssignmentStatus(c.external.assignmentStatus) && (
                                                        <span className="text-xs text-foreground break-words">{t('recoverRequests.conflictResolution.engineer', { name: c.external.engineerName ?? '—' })}</span>
                                                    )}
                                                    {hasAssignmentStatus(c.external.assignmentStatus) && <RunbookStatsBlock side={c.external} t={t} />}
                                                    {!sameVersion && (
                                                        <span className="text-xs break-words">
                                                            <span className="text-muted-foreground">{t('recoverRequests.conflictResolution.dateVaultedLabel', 'Date Vaulted:')}</span>{' '}
                                                            <span className="text-foreground">{c.external.vaultdatetime != null && String(c.external.vaultdatetime).trim() !== '' ? formatVaultDate(c.external.vaultdatetime) : t('recoverRequests.conflictResolution.managementOnly', 'Management Only')}</span>
                                                            {datesDiffer && externalIsNewer && <span className="ml-1.5 text-xs font-medium text-green-600">{t('recoverRequests.conflictResolution.newest', 'Newest')}</span>}
                                                            {datesDiffer && manifestIsNewer && <span className="ml-1.5 text-xs font-medium text-red-600">{t('recoverRequests.conflictResolution.oldest', 'Oldest')}</span>}
                                                        </span>
                                                    )}
                                                </div>
                                                <label className="flex items-center shrink-0 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name={`resolve-${c.objectId}`}
                                                        checked={selectNew}
                                                        onChange={() => setSelections((prev) => ({ ...prev, [c.objectId]: c.externalRequestId }))}
                                                        className="h-4 w-4"
                                                    />
                                                    <span className="sr-only">{t('recoverRequests.conflictResolution.selectNew', 'Select new')}</span>
                                                </label>
                                            </div>
                                            {/* Recovery Set column (right) */}
                                            <div className="flex min-w-0 rounded border border-border bg-muted/20 p-1.5 items-center justify-between gap-2">
                                                <div className="flex flex-col min-w-0 gap-0.5 flex-1">
                                                    {hasAssignmentStatus(c.manifest.assignmentStatus) && (
                                                        <span className={cn('text-xs font-medium rounded px-1.5 py-0.5 w-fit', getStatusPillClass(c.manifest.assignmentStatus!))}>{c.manifest.assignmentStatus}</span>
                                                    )}
                                                    {hasAssignmentStatus(c.manifest.assignmentStatus) && (
                                                        <span className="text-xs text-foreground break-words">{t('recoverRequests.conflictResolution.engineer', { name: c.manifest.engineerName ?? '—' })}</span>
                                                    )}
                                                    {hasAssignmentStatus(c.manifest.assignmentStatus) && <RunbookStatsBlock side={c.manifest} t={t} />}
                                                    {!sameVersion && (
                                                        <span className="text-xs break-words">
                                                            <span className="text-muted-foreground">{t('recoverRequests.conflictResolution.dateVaultedLabel', 'Date Vaulted:')}</span>{' '}
                                                            <span className="text-foreground">{c.manifest.vaultdatetime != null && String(c.manifest.vaultdatetime).trim() !== '' ? formatVaultDate(c.manifest.vaultdatetime) : t('recoverRequests.conflictResolution.managementOnly', 'Management Only')}</span>
                                                            {datesDiffer && manifestIsNewer && <span className="ml-1.5 text-xs font-medium text-green-600">{t('recoverRequests.conflictResolution.newest', 'Newest')}</span>}
                                                            {datesDiffer && externalIsNewer && <span className="ml-1.5 text-xs font-medium text-red-600">{t('recoverRequests.conflictResolution.oldest', 'Oldest')}</span>}
                                                        </span>
                                                    )}
                                                </div>
                                                <label className="flex items-center shrink-0 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name={`resolve-${c.objectId}`}
                                                        checked={selectExisting}
                                                        disabled={c.manifestRequestId == null}
                                                        onChange={() => c.manifestRequestId != null && setSelections((prev) => ({ ...prev, [c.objectId]: c.manifestRequestId! }))}
                                                        className="h-4 w-4"
                                                    />
                                                    <span className="sr-only">{t('recoverRequests.conflictResolution.selectExisting', 'Select existing')}</span>
                                                </label>
                                            </div>
                                        </div>
                                    )
                                })}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={!allResolved || isSubmitting}>
                        {isSubmitting ? t('common.saving', 'Saving...') : t('recoverRequests.conflictResolution.addWithResolutions', 'Add to set with resolutions')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
